import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { BookingFormulaDto } from './booking-formula.model'
import { pick } from 'ramda'
import { catchError, concatMap, tap, toArray } from 'rxjs/operators'
import { EMPTY, Observable, of } from 'rxjs'
import { Toaster } from '@tokeet/tv3-platform'

@Injectable()
export class BookingFormulaService {
  constructor(private http: HttpClient, private toaster: Toaster) {}

  getChannelRentalFormula(channelId: string, rentalId: string): Observable<BookingFormulaDto> {
    const url = '@api/bookingformula/match'

    return this.http
      .post<BookingFormulaDto>(url, { channel_id: channelId, rental_id: rentalId })
      .pipe(catchError(() => of(null)))
  }

  getFormula(id: string) {
    const url = `@api/bookingformula/${id}`

    return this.http.get<BookingFormulaDto>(url).pipe(catchError(() => of(null)))
  }

  getFormulasByChannel(channelId: string) {
    const url = `@api/bookingformula/all/channel`

    return this.http.post<BookingFormulaDto[]>(url, { channel_id: channelId }).pipe(catchError(() => of([])))
  }

  createFormula(formula: Partial<BookingFormulaDto>) {
    const url = `@api/bookingformula`

    return this.http.post<BookingFormulaDto>(url, formula).pipe(
      tap(() => {
        this.toaster.success($localize`Booking formula created successfully`)
      }),
      catchError((error) => {
        this.toaster.error(null, 'Error', error)
        return EMPTY
      })
    )
  }

  updateFormula(id: string, formula: Partial<BookingFormulaDto>) {
    const url = `@api/bookingformula/${id}`

    return this.http.put<BookingFormulaDto>(url, { pkey: id, ...formula }).pipe(
      tap(() => {
        this.toaster.success($localize`Booking formula updated successfully`)
      }),
      catchError((error) => {
        this.toaster.error(null, 'Error', error)
        return EMPTY
      })
    )
  }

  activateFormula(id: string) {
    const url = `@api/bookingformula/${id}/unpause`

    return this.http.put<BookingFormulaDto>(url, {}).pipe(
      tap(() => this.toaster.success($localize`Formula activated successfully.`)),
      catchError((error) => {
        this.toaster.error(null, 'Error', error)
        return EMPTY
      })
    )
  }

  deactivateFormula(id: string) {
    const url = `@api/bookingformula/${id}/pause`

    return this.http.put<BookingFormulaDto>(url, {}).pipe(
      tap(() => this.toaster.success($localize`Formula deactivated successfully.`)),
      catchError((error) => {
        this.toaster.error(null, 'Error', error)
        return EMPTY
      })
    )
  }

  deleteFormula(id: string) {
    const url = `@api/bookingformula/${id}`

    return this.http.delete(url).pipe(
      tap(() => this.toaster.success($localize`Formula deleted successfully.`)),
      catchError((error) => {
        this.toaster.error(null, 'Error', error)
        return EMPTY
      })
    )
  }

  deleteMultipleFormulas(ids: string[]) {
    const deleteOne = (id: string) => {
      const url = `@api/bookingformula/${id}`
      return this.http.delete(url)
    }

    return of(...ids).pipe(
      concatMap((id) => deleteOne(id)),
      toArray(),
      catchError((error) => {
        this.toaster.error(null, 'Error', error)
        return EMPTY
      }),
      tap(() => this.toaster.success($localize`Formulas deleted successfully.`))
    )
  }
}
